import { useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import { gqlMutate } from '@hapana/shared/web/services/requests.service'
import {
  generateStripeOnboardingLinkMutation,
  refreshStripeStatusMutation,
} from '_services/graphql/payment-gateway.mutations'
import { getSitePaymentGatewayOnboardingStatus } from '_services/payment-gateway.service'

export const STRIPE_ONBOARDING_SEARCH_PARAM = 'onboarding'

export const StripeOnboardingLanding = {
  Return: 'return',
  Refresh: 'refresh',
} as const

interface GenerateStripeOnboardingLinkResponse {
  generateStripeOnboardingLink: { url: string; expiresAt: number } | null
}

const refreshStripeStatus = (siteId: string): Promise<unknown> =>
  gqlMutate({
    mutation: refreshStripeStatusMutation,
    variables: { siteId },
    options: { isV1: false },
  })

const generateStripeOnboardingLink = (
  siteId: string
): Promise<GenerateStripeOnboardingLinkResponse> =>
  gqlMutate<GenerateStripeOnboardingLinkResponse>({
    mutation: generateStripeOnboardingLinkMutation,
    variables: { siteId },
    options: { isV1: false },
  })

export const useStripeOnboardingReturn = (): void => {
  const { siteId } = useParams<{ siteId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const latestSearch = useRef(location.search)
  latestSearch.current = location.search
  const queryClient = useQueryClient()
  const landing = searchParams.get(STRIPE_ONBOARDING_SEARCH_PARAM)

  const clearLanding = () => {
    const remaining = new URLSearchParams(latestSearch.current)
    remaining.delete(STRIPE_ONBOARDING_SEARCH_PARAM)
    setSearchParams(remaining, { replace: true })
  }

  const { mutate: refreshStatus } = useMutation(refreshStripeStatus, {
    onError: (error) => {
      console.warn('Stripe onboarding status refresh failed', error)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([getSitePaymentGatewayOnboardingStatus.key])
      clearLanding()
    },
  })

  const { mutate: resumeOnboarding } = useMutation(generateStripeOnboardingLink, {
    onSuccess: (data) => {
      const url = data?.generateStripeOnboardingLink?.url
      if (url) {
        window.location.replace(url)
        return
      }
      clearLanding()
    },
    onError: clearLanding,
  })

  useEffect(() => {
    if (!siteId) {
      return
    }
    if (landing === StripeOnboardingLanding.Return) {
      refreshStatus(siteId)
    } else if (landing === StripeOnboardingLanding.Refresh) {
      resumeOnboarding(siteId)
    }
  }, [siteId, landing])
}
